import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { scrollTo } from "./Navbar";

const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_2e5c0fe7-21bd-4845-ae98-db1e0d67887b/artifacts/4372cda16a7216ce_barb.webp";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16">
      <motion.div style={{ opacity: glowOpacity }} className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-ember/10 blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7 relative z-10">
          <RevealLine delay={0.35}>
            <span className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.25em] uppercase text-ember border border-ember/30 bg-ember/10 rounded-full px-4 py-1.5">
              <MapPin size={12} /> Atri — Borgo Ducale, Teramo
            </span>
          </RevealLine>

          <h1 className="mt-8 font-serif font-bold tracking-tight leading-[0.95] text-ivory">
            <RevealLine delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl">MODICO</RevealLine>
            <RevealLine delay={0.62} className="text-5xl sm:text-7xl lg:text-8xl">
              <span className="italic font-medium text-ember">Barberia</span>
            </RevealLine>
          </h1>

          <RevealLine delay={0.8}>
            <p className="mt-8 max-w-md text-base sm:text-lg text-sand leading-relaxed">
              Il rito della cura, senza fretta. Un'ora dedicata solo a te,
              tra legno di noce, pietra secolare e lama a mano libera.
            </p>
          </RevealLine>

          <RevealLine delay={0.95}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-booking-cta"
                onClick={() => scrollTo("prenota")}
                className="group relative px-8 py-4 bg-ember hover:bg-ember-hover text-white text-sm font-mono uppercase tracking-widest rounded-sm transition-all duration-300 shadow-[0_0_32px_rgba(230,90,30,0.35)] hover:shadow-[0_0_44px_rgba(230,90,30,0.5)]"
              >
                Prenota il tuo posto
              </button>
              <button
                data-testid="hero-philosophy-cta"
                onClick={() => scrollTo("filosofia")}
                className="px-8 py-4 border border-white/15 hover:border-ember/50 text-sand hover:text-ivory text-sm font-mono uppercase tracking-widest rounded-sm transition-colors duration-300"
              >
                La filosofia
              </button>
            </div>
          </RevealLine>

          <RevealLine delay={1.1}>
            <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-mono uppercase tracking-widest text-stone">
              <Clock size={13} className="text-ember" />
              <span className="whitespace-nowrap">Mar – Gio 10–20 <span className="text-ember">/</span></span>
              <span className="whitespace-nowrap">Ven 9–20 <span className="text-ember">/</span></span>
              <span className="whitespace-nowrap">Sab 9–18</span>
            </div>
          </RevealLine>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 bg-ember/15 blur-2xl rounded-2xl pointer-events-none" />
            <div className="relative overflow-hidden rounded-lg border border-white/10 aspect-[4/5] max-h-[70vh] w-full">
              <motion.img
                src={HERO_IMG}
                alt="Sala principale di Modico Barberia con logo MB arancione e poltrone in pelle nera"
                style={{ y: imgY }}
                className="w-full h-full object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-void/60 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 1.3 }}
              className="absolute -bottom-5 -left-5 bg-espresso border border-white/10 rounded-md px-5 py-4 backdrop-blur-sm"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone">Il maestro</p>
              <p className="font-serif text-xl text-ivory italic">Daniele De Sanctis</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
